import { useSelector } from 'react-redux';
import { useEffectOnce } from 'react-use';
import { twMerge } from 'tailwind-merge';

import { IState } from 'models/reducers/i-state.interface';
import { ITopOffersTradePreviewDetails } from 'models/top-offers/i-top-offers';
import { ITradeAsset } from 'models/trade/i-trade-details.interface';
import { ITradeOfferSale } from 'models/trade/i-trade-offer-sale.interface';
import { TradeOfferStatus } from 'models/trade/i-trade-offer.interface';

import MarketVenueCardMain from 'routes/market/marketVenueCard/marketVenueCardMain/MarketVenueCardMain';

import Button from 'components/buttons/Button';
import ElementLoader from 'components/element-loader/ElementLoader';
import NftCardLabelPanel from 'components/nftCard/_new/nftCardLabelPanel/NftCardLabelPanel';
import NftCardShares from 'components/nftCard/_new/nftCardShares/NftCardShares';
import NftCardValue from 'components/nftCard/_new/nftCardValue/NftCardValue';
import NftCardWrapper from 'components/nftCard/nftCardWrapper/NftCardWrapper';
import SoldBadge from 'components/soldBadge/SoldBadge';

import { INftParams } from 'hooks/events/useEventsParams';
import { useNftCardExpectedAnnualYield } from 'hooks/shared/useNftCardExpectedAnnualYield';
import { useCurrency } from 'hooks/useCurrency';
import { useDecimalPrice } from 'hooks/useDecimalPrice';
import { useTraits } from 'hooks/useTraits';

import { renderInt } from 'utils/render.utils';

interface ITopOffersNftCardMainProps {
  asset: ITradeAsset;
}

const TopOffersNftCardMain = ({ asset }: ITopOffersNftCardMainProps) => {
  const { id, category } = useTraits(asset.traits);

  return (
    <MarketVenueCardMain
      id={+id}
      address={asset.address}
      availableShares={asset.total_shares}
      cardUrl={asset.image_original_url || ''}
      cardAnimationUrl={asset.animation_url || ''}
      category={category}
      name={asset.name || '-'}
      iconUrl={asset.marker_url}
    />
  );
};

const TopOffersNftCardAnnualYield = ({ nftId }: INftParams) => {
  const { expectedAnnualYield, loadingExpectedAnnualYield, onFetchExpectedAnnualYield } =
    useNftCardExpectedAnnualYield(nftId);

  useEffectOnce(() => {
    onFetchExpectedAnnualYield();
  });

  return (
    <ElementLoader loading={loadingExpectedAnnualYield}>
      <div className="tw-bg-cardBackground tw-rounded-md">
        <NftCardLabelPanel label="Estimated Annual Yield">
          <p>{expectedAnnualYield || '-'}</p>
        </NftCardLabelPanel>
      </div>
    </ElementLoader>
  );
};

interface ITopOffersNftCardProps {
  offer: ITradeOfferSale;
  onPurchase: (itemPreviewDetails: ITopOffersTradePreviewDetails) => void;
}

const TopOffersNftCard = ({ offer, onPurchase }: ITopOffersNftCardProps) => {
  const { offersAssets } = useSelector((state: IState) => state.topOffers);
  const offerAsset = offersAssets.find((offersAsset) => offersAsset.id === offer.asset_id);
  const currency = useCurrency(offer.currency_id);
  const price = useDecimalPrice(parseInt(offer.price));
  const sold = offer.status === TradeOfferStatus.FINISHED;

  const handleClick = () => {
    if (!!offerAsset) {
      const itemPreviewDetails: ITopOffersTradePreviewDetails = {
        tradeDetails: offer,
        assetDetails: offerAsset,
      };

      onPurchase(itemPreviewDetails);
    }
  };

  return (
    <NftCardWrapper twClassName="tw-w-[280px] tw-bg-backgroundColorDark relative">
      {sold && (
        <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center [transform:rotate(-15deg)]">
          <SoldBadge />
        </div>
      )}
      <div className={twMerge('tw-min-w-[236px]', sold && 'tw-opacity-50')}>
        {offerAsset && (
          <>
            <TopOffersNftCardMain asset={offerAsset} />
            <div className="tw-mt-3 tw-flex tw-flex-col tw-justify-around tw-gap-1">
              <div className="tw-bg-cardBackground tw-rounded-md">
                <NftCardLabelPanel className="tw-bg-cardBackground" label="Shares">
                  <NftCardShares>{offer.amount}</NftCardShares>
                </NftCardLabelPanel>
              </div>

              <div className="tw-bg-cardBackground tw-rounded-md">
                <NftCardLabelPanel className="tw-bg-cardBackground" label="Price">
                  <NftCardValue currency={currency} value={renderInt(price)} />
                </NftCardLabelPanel>
              </div>
              <TopOffersNftCardAnnualYield nftId={offerAsset.token_id} />
            </div>
          </>
        )}
        <Button size="medium" twClassName="tw-w-full tw-mt-3" onClick={handleClick} disabled={sold} requireWeb3>
          Buy now
        </Button>
      </div>
    </NftCardWrapper>
  );
};

export default TopOffersNftCard;
